import React, { useState, useRef } from 'react';
import {
    Heart,
    Brain,
    Users,
    Target,
    CheckCircle,
    ArrowRight,
    Sparkles,
    Shield,
    TrendingUp,
    Lightbulb,
    MessageCircle,
    Clock,
    Award,
    UserCheck,
    Compass,
    Video,
    Calendar,
    Euro,
    X
} from 'lucide-react';
import BookingForm from '../components/BookingForm';

const CoachingPage = () => {
    const [showBookingModal, setShowBookingModal] = useState(false);
    const bookingRef = useRef(null);

    const scrollToBooking = () => {
        bookingRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section - Premium Dark Design */}
            <section className="relative pt-32 pb-32 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 overflow-hidden">
                {/* Background decorative blurs */}
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium text-white mb-8">
                            <Heart className="text-indigo-400" size={18} />
                            <span>Relationship Coaching</span>
                        </div>

                        {/* Main Title - FĂRĂ GRADIENT */}
                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight tracking-tight">
                            Coaching relațional:{' '}
                            <span className="text-white">
                                claritate, echilibru și relații construite conștient
                            </span>
                        </h1>

                        {/* Description */}
                        <p className="text-xl md:text-2xl text-slate-300 mb-10 leading-relaxed max-w-3xl mx-auto font-light">
                            Relationship Coaching-ul din cadrul Affinity Club este gândit pentru oameni care nu mai vor să repete aceleași
                            scenarii. Lucrăm cu tine – <strong className="text-white font-semibold">individual sau în cuplu</strong> – pentru a înțelege ce se întâmplă în spatele reacțiilor,
                            alegerilor și tiparelor tale relaționale.
                        </p>

                        {/* Info Card */}
                        <div className="bg-white/5 backdrop-blur-md rounded-3xl p-8 border border-white/10 mb-10 max-w-3xl mx-auto">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl">
                                    <Heart className="text-white" size={24} />
                                </div>
                                <p className="text-lg text-slate-200 leading-relaxed text-left">
                                    Sesiunile sunt susținute de{' '}
                                    <strong className="text-white">psihologi și psihoterapeuți formați</strong>,
                                    cu experiență în lucru cu dinamici de cuplu, atașament, anxietate relațională și reconstrucție emoțională.
                                </p>
                            </div>
                        </div>

                        {/* CTA Button - ALB CU TEXT NEGRU, FĂRĂ GRADIENT */}
                        <button
                            onClick={scrollToBooking}
                            className="group bg-white text-slate-900 px-12 py-5 rounded-full font-bold text-lg hover:bg-slate-100 transition-all duration-300 shadow-2xl hover:shadow-white/25 inline-flex items-center gap-3"
                        >
                            Programează o sesiune de coaching
                            <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform" />
                        </button>

                        {/* Trust indicators */}
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-emerald-400" />
                                <span>Confidențialitate 100%</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-emerald-400" />
                                <span>Psihologi certificați</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle size={16} className="text-emerald-400" />
                                <span>Online sau față în față</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tipuri de Ședințe - NOU */}
            <section className="py-20 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center bg-rose-100 text-rose-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            <Calendar size={16} className="mr-2" />
                            Tipuri de ședințe
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Alege formatul potrivit pentru tine</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                            Oferim două tipuri de sesiuni, adaptate nevoilor tale specifice.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {/* Consiliere Individuală */}
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-200 shadow-lg hover:shadow-2xl transition-all">
                            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                                <UserCheck className="text-blue-600" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-3">Consiliere Individuală</h3>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                                Sesiune 1:1 pentru explorarea tiparelor relaționale, stilului de atașament, anxietății
                                în relații și dezvoltarea unei relații mai sănătoase cu tine.
                            </p>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-center text-slate-700">
                                    <Clock className="mr-3 text-blue-600" size={18} />
                                    <span>50 minute / ședință</span>
                                </div>
                                <div className="flex items-center text-slate-700">
                                    <Video className="mr-3 text-blue-600" size={18} />
                                    <span>Online (Zoom / Google Meet)</span>
                                </div>
                                <div className="flex items-center text-slate-700">
                                    <Shield className="mr-3 text-blue-600" size={18} />
                                    <span>Confidențialitate garantată</span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-blue-200">
                                <div>
                                    <div className="text-3xl font-bold text-blue-600">50€</div>
                                    <div className="text-sm text-slate-600">per ședință</div>
                                </div>
                                <button
                                    onClick={scrollToBooking}
                                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                                >
                                    Programează
                                </button>
                            </div>
                        </div>

                        {/* Terapie de Cuplu */}
                        <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border-2 border-rose-200 shadow-lg hover:shadow-2xl transition-all">
                            <div className="w-16 h-16 bg-rose-100 rounded-xl flex items-center justify-center mb-6">
                                <Users className="text-rose-600" size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 mb-3">Terapie de Cuplu</h3>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                                Sesiune pentru cupluri care vor să îmbunătățească comunicarea, să rezolve conflicte
                                recurente sau să reconstruiască încrederea și intimitatea.
                            </p>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-center text-slate-700">
                                    <Clock className="mr-3 text-rose-600" size={18} />
                                    <span>50 minute / ședință</span>
                                </div>
                                <div className="flex items-center text-slate-700">
                                    <Video className="mr-3 text-rose-600" size={18} />
                                    <span>Online (Zoom / Google Meet)</span>
                                </div>
                                <div className="flex items-center text-slate-700">
                                    <Heart className="mr-3 text-rose-600" size={18} />
                                    <span>Spațiu sigur pentru ambii parteneri</span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t border-rose-200">
                                <div>
                                    <div className="text-3xl font-bold text-rose-600">80€</div>
                                    <div className="text-sm text-slate-600">per ședință</div>
                                </div>
                                <button
                                    onClick={scrollToBooking}
                                    className="bg-rose-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-rose-700 transition-colors"
                                >
                                    Programează
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Echipa de Psihologi */}
            <section className="py-20 px-6 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            <Users size={16} className="mr-2" />
                            Echipa
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Psihologii care te vor ghida în proces</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                            Relationship Coaching-ul Affinity Club este dezvoltat și susținut de psihologi cu formări complexe
                            în psihoterapie integrativă, psihologie clinică și intervenții orientate pe corp și respirație conștientă.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
                        {/* Adela Silaghi */}
                        <div className="bg-white rounded-2xl p-8 border-2 border-purple-200 shadow-lg hover:shadow-2xl transition-all">
                            <div className="relative mb-6">
                                <div className="w-48 h-48 mx-auto rounded-full overflow-hidden border-4 border-purple-100 shadow-xl">
                                    <img
                                        src="https://affinityclub.agency/wp-content/uploads/2025/12/Adela-Silaghi.jpg"
                                        alt="Adela Silaghi"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>

                            <h3 className="text-2xl font-bold text-slate-900 text-center mb-2">Adela Silaghi</h3>
                            <p className="text-center text-purple-600 font-semibold mb-6">Psiholog clinician & psihoterapeut integrativ</p>

                            <p className="text-slate-700 mb-4 leading-relaxed">
                                Absolventă a Facultății de Psihologie – Spiru Haret, cu Master în Psihologie Clinică și Intervenție Psihologică,
                                Adela are o formare complexă în psihoterapie integrativă și în terapii expresiv-creative (ARTEPSY).
                            </p>

                            <p className="text-slate-700 leading-relaxed">
                                A urmat formări în psihoterapie corporală bazată pe mindfulness, hipnoză, psihoterapie transpersonală și
                                respirație Pneuma. Aduce un stil calm și profund, în care corpul, mintea și emoțiile sunt privite împreună.
                            </p>

                            <div className="mt-6 flex flex-wrap gap-2">
                                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">Atașament</span>
                                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">Anxietate</span>
                                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">Terapie cuplu</span>
                            </div>
                        </div>

                        {/* Roxana Carmen Cîrstea */}
                        <div className="bg-white rounded-2xl p-8 border-2 border-cyan-200 shadow-lg hover:shadow-2xl transition-all">
                            <div className="relative mb-6">
                                <div className="w-48 h-48 mx-auto rounded-full overflow-hidden border-4 border-cyan-100 shadow-xl">
                                    <img
                                        src="https://affinityclub.agency/wp-content/uploads/2025/12/Roxana-Carmen-Cirstea.jpg"
                                        alt="Roxana Carmen Cîrstea"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>

                            <h3 className="text-2xl font-bold text-slate-900 text-center mb-2">Roxana Carmen Cîrstea</h3>
                            <p className="text-center text-cyan-600 font-semibold mb-6">Psihoterapeut transpersonal & coach</p>

                            <p className="text-slate-700 mb-4 leading-relaxed">
                                Roxana este absolventă a Facultății de Psihologie și Științele Educației – Universitatea Spiru Haret, cu Master
                                în psihologie și formare în Psihoterapie Transpersonală în cadrul Asociației Europene de Psihologie Transpersonală.
                            </p>

                            <p className="text-slate-700 leading-relaxed">
                                A parcurs programe de coaching, consiliere de dezvoltare personală și formare ca trainer de Qigong,
                                fiind certificată COPSI în Pneuma Breathwork. Te ajută să înțelegi sensul experiențelor tale.
                            </p>

                            <div className="mt-6 flex flex-wrap gap-2">
                                <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm">Transpersonal</span>
                                <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm">Breathwork</span>
                                <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm">Coaching</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Cum funcționează */}
            <section className="py-20 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            <Target size={16} className="mr-2" />
                            Proces simplu
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Cum funcționează?</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                            Am simplificat procesul de programare pentru a-ți face experiența cât mai ușoară.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl font-bold text-purple-600">1</span>
                            </div>
                            <h3 className="font-bold text-slate-900 mb-2">Alegi psihologul</h3>
                            <p className="text-slate-600 text-sm">Selectezi specialistul cu care rezonezi</p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl font-bold text-rose-600">2</span>
                            </div>
                            <h3 className="font-bold text-slate-900 mb-2">Alegi tipul ședinței</h3>
                            <p className="text-slate-600 text-sm">Individuală sau pentru cuplu</p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl font-bold text-blue-600">3</span>
                            </div>
                            <h3 className="font-bold text-slate-900 mb-2">Programezi data și ora</h3>
                            <p className="text-slate-600 text-sm">Din sloturile disponibile</p>
                        </div>

                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl font-bold text-green-600">4</span>
                            </div>
                            <h3 className="font-bold text-slate-900 mb-2">Confirmare & plată</h3>
                            <p className="text-slate-600 text-sm">Primești linkul de plată pe email</p>
                        </div>
                    </div>

                    <div className="mt-12 bg-amber-50 border border-amber-200 rounded-2xl p-6 max-w-3xl mx-auto">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Lightbulb className="text-amber-600" size={20} />
                            </div>
                            <div>
                                <h4 className="font-bold text-amber-900 mb-1">Bun de știut</h4>
                                <p className="text-amber-800 text-sm leading-relaxed">
                                    După ce trimiți cererea, psihologul o va analiza și îți va trimite un email de confirmare
                                    cu linkul de plată securizat. Plata se face prin Stripe, iar ședința devine activă imediat după achitare.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ce urmărim în coaching */}
            <section className="py-20 px-6 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                                <Compass size={16} className="mr-2" />
                                Ce explorăm
                            </div>
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Ce poți explora în sesiuni</h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            {[
                                'Identificarea tiparelor care se repetă în relațiile tale',
                                'Clarificarea nevoilor tale emoționale reale',
                                'Înțelegerea stilului tău de atașament și impactul lui',
                                'Dezvoltarea unei comunicări mai autentice și mai calme',
                                'Reconstrucția încrederii după despărțiri sau trădări',
                                'Gestionarea anxietății de respingere sau abandon',
                                'Setarea limitelor sănătoase în relații',
                                'Alinierea dintre ce îți dorești și ce accepți în relație'
                            ].map((item, idx) => (
                                <div key={idx} className="bg-white rounded-xl p-4 border border-slate-200 flex items-start">
                                    <CheckCircle className="text-emerald-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                                    <span className="text-slate-700">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Beneficii */}
            <section className="py-20 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                                <TrendingUp size={16} className="mr-2" />
                                Rezultate
                            </div>
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">Ce te ajută să obții</h2>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                                <Brain className="text-purple-600 mb-4" size={32} />
                                <h3 className="font-bold text-slate-900 mb-2">Claritate emoțională</h3>
                                <p className="text-slate-600 text-sm">Înțelegi ce simți, dincolo de reacția de moment, și de ce reacționezi așa.</p>
                            </div>

                            <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-xl p-6 border border-rose-200">
                                <MessageCircle className="text-rose-600 mb-4" size={32} />
                                <h3 className="font-bold text-slate-900 mb-2">Comunicare autentică</h3>
                                <p className="text-slate-600 text-sm">O comunicare mai calmă și mai clară cu partenerul sau viitorul partener.</p>
                            </div>

                            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                                <Shield className="text-blue-600 mb-4" size={32} />
                                <h3 className="font-bold text-slate-900 mb-2">Reducerea anxietății</h3>
                                <p className="text-slate-600 text-sm">Mai puțină frică de respingere, abandon sau pierderea controlului.</p>
                            </div>

                            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                                <Target className="text-emerald-600 mb-4" size={32} />
                                <h3 className="font-bold text-slate-900 mb-2">Alegeri conștiente</h3>
                                <p className="text-slate-600 text-sm">Capacitatea de a discerne între ce e sănătos și ce e doar familiar.</p>
                            </div>

                            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
                                <Heart className="text-amber-600 mb-4" size={32} />
                                <h3 className="font-bold text-slate-900 mb-2">Relație cu tine</h3>
                                <p className="text-slate-600 text-sm">O relație mai bună cu tine însuți/însăți, baza oricărei relații sănătoase.</p>
                            </div>

                            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                                <Compass className="text-indigo-600 mb-4" size={32} />
                                <h3 className="font-bold text-slate-900 mb-2">Plan personalizat</h3>
                                <p className="text-slate-600 text-sm">Un traseu clar pentru a construi relații mai stabile și mai așezate.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Formularul de Booking - INTEGRAT */}
            <section ref={bookingRef} className="py-20 px-6 bg-gradient-to-br from-purple-50 via-white to-rose-50" id="booking">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            <Calendar size={16} className="mr-2" />
                            Programare
                        </div>
                        <h2 className="text-4xl font-bold text-slate-900 mb-4">Programează o sesiune</h2>
                        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                            Completează formularul pentru a solicita o programare.
                            Vei primi confirmarea pe email în cel mai scurt timp.
                        </p>
                    </div>

                    <BookingForm />
                </div>
            </section>

            {/* De ce noi */}
            <section className="py-20 px-6 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                                <Sparkles size={16} className="mr-2" />
                                De ce noi
                            </div>
                            <h2 className="text-4xl font-bold text-slate-900 mb-6">De ce Affinity Club</h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                <Award className="text-purple-600 mb-4" size={28} />
                                <h3 className="font-bold text-slate-900 mb-2">Psihologi formați</h3>
                                <p className="text-slate-600">
                                    Lucrezi cu psihologi și psihoterapeuți cu formări complete, nu cu "coachi" fără fundament clinic.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                <Brain className="text-blue-600 mb-4" size={28} />
                                <h3 className="font-bold text-slate-900 mb-2">Integrat cu profilul tău</h3>
                                <p className="text-slate-600">
                                    Procesul poate fi integrat cu rezultatele testelor tale din platformă pentru un plan mai precis.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                <Shield className="text-emerald-600 mb-4" size={28} />
                                <h3 className="font-bold text-slate-900 mb-2">Siguranță & confidențialitate</h3>
                                <p className="text-slate-600">
                                    Un cadru sigur care îmbină siguranța psihoterapiei cu abordarea orientată spre soluții.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
                                <Heart className="text-rose-600 mb-4" size={28} />
                                <h3 className="font-bold text-slate-900 mb-2">Maturitate, nu trucuri</h3>
                                <p className="text-slate-600">
                                    Accentul este pe maturitate emoțională, nu pe jocuri sau "strategii de dating".
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Final */}
            <section className="py-28 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-white relative overflow-hidden">
                {/* Background decorative blurs */}
                <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight">
                        Pregătit(ă) să construiești relații mai sănătoase?
                    </h2>
                    <p className="text-xl md:text-2xl mb-10 opacity-90 leading-relaxed font-light max-w-2xl mx-auto">
                        Dacă simți că a venit momentul să privești cu mai multă claritate la modul în care iubești și alegi,
                        te așteptăm într-o sesiune de coaching.
                    </p>

                    <button
                        onClick={scrollToBooking}
                        className="group bg-white text-slate-900 px-12 py-5 rounded-full font-bold text-lg hover:bg-slate-100 transition-all duration-300 shadow-2xl hover:shadow-white/25 inline-flex items-center gap-3"
                    >
                        Programează prima sesiune
                        <ArrowRight size={22} className="group-hover:translate-x-2 transition-transform" />
                    </button>

                    <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
                        <div className="flex items-center gap-2">
                            <Shield size={16} className="text-emerald-400" />
                            <span>Online</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Shield size={16} className="text-emerald-400" />
                            <span>Confidențial</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Shield size={16} className="text-emerald-400" />
                            <span>Ghidat de psihologi formați</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CoachingPage;